import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import { ALL_ISSUE_TYPES_VALUE } from './coverageHoursPanelUtils';

export type PanelBaseline = {
  categoryValue: string | null;
  issueTypeValue: string;
  savedTeams: TeamCoverageEntry[];
};

export type PanelState = {
  categoryValue: string | null;
  issueTypeValue: string;
  savedTeams: TeamCoverageEntry[];
  // One flag shared by every team form, which holds only while formMode keeps a
  // single form open at a time. Key it by form id if that ever relaxes.
  hasTeamFormConflict: boolean;
  isConfirmingClose: boolean;
  saveError: boolean;
  isSaving: boolean;
  // What the panel opened with. A create opens empty; an edit opens holding the
  // pairing it loaded, and closing has to return there rather than to empty.
  baseline: PanelBaseline;
};

const CREATE_BASELINE: PanelBaseline = {
  categoryValue: null,
  issueTypeValue: ALL_ISSUE_TYPES_VALUE,
  savedTeams: [],
};

export const INITIAL_PANEL_STATE: PanelState = {
  ...CREATE_BASELINE,
  hasTeamFormConflict: false,
  isConfirmingClose: false,
  saveError: false,
  isSaving: false,
  baseline: CREATE_BASELINE,
};

export function initialPanelState(baseline: PanelBaseline): PanelState {
  return { ...INITIAL_PANEL_STATE, ...baseline, baseline };
}

export type PanelAction =
  | { type: 'CATEGORY_CHANGED'; categoryValue: string; issueTypeValue: string }
  | { type: 'ISSUE_TYPE_CHANGED'; issueTypeValue: string }
  | { type: 'TEAM_SAVED'; entry: TeamCoverageEntry }
  | { type: 'TEAM_DISCARDED'; id: string }
  | { type: 'TEAM_FORM_CONFLICT_CHANGED'; hasConflict: boolean }
  | { type: 'CLOSE_REQUESTED' }
  | { type: 'CLOSE_CANCELLED' }
  | { type: 'CLOSED_PANEL' }
  | { type: 'SAVE_STARTED' }
  | { type: 'SAVE_FAILED' }
  | { type: 'SAVE_SETTLED' };

export function changeCategory(
  categoryValue: string,
  issueTypeValue: string
): PanelAction {
  return { type: 'CATEGORY_CHANGED', categoryValue, issueTypeValue };
}

export function changeIssueType(issueTypeValue: string): PanelAction {
  return { type: 'ISSUE_TYPE_CHANGED', issueTypeValue };
}

export function saveTeam(entry: TeamCoverageEntry): PanelAction {
  return { type: 'TEAM_SAVED', entry };
}

export function discardTeam(id: string): PanelAction {
  return { type: 'TEAM_DISCARDED', id };
}

export function changeTeamFormConflict(hasConflict: boolean): PanelAction {
  return { type: 'TEAM_FORM_CONFLICT_CHANGED', hasConflict };
}

export function requestClose(): PanelAction {
  return { type: 'CLOSE_REQUESTED' };
}

export function cancelClose(): PanelAction {
  return { type: 'CLOSE_CANCELLED' };
}

export function closePanel(): PanelAction {
  return { type: 'CLOSED_PANEL' };
}

export function startSave(): PanelAction {
  return { type: 'SAVE_STARTED' };
}

export function failSave(): PanelAction {
  return { type: 'SAVE_FAILED' };
}

export function settleSave(): PanelAction {
  return { type: 'SAVE_SETTLED' };
}

export function panelReducer(state: PanelState, action: PanelAction): PanelState {
  switch (action.type) {
    case 'CATEGORY_CHANGED':
      return {
        ...state,
        categoryValue: action.categoryValue,
        issueTypeValue: action.issueTypeValue,
      };
    case 'ISSUE_TYPE_CHANGED':
      return { ...state, issueTypeValue: action.issueTypeValue };
    case 'TEAM_SAVED': {
      const idx = state.savedTeams.findIndex(
        team => team.id === action.entry.id
      );

      if (idx >= 0) {
        const savedTeams = [...state.savedTeams];

        savedTeams[idx] = action.entry;

        return { ...state, savedTeams };
      }

      return { ...state, savedTeams: [...state.savedTeams, action.entry] };
    }

    case 'TEAM_DISCARDED':
      return {
        ...state,
        savedTeams: state.savedTeams.filter(team => team.id !== action.id),
      };
    case 'TEAM_FORM_CONFLICT_CHANGED':
      if (action.hasConflict === state.hasTeamFormConflict) {
        return state;
      }

      return { ...state, hasTeamFormConflict: action.hasConflict };
    case 'CLOSE_REQUESTED':
      return { ...state, isConfirmingClose: true };
    case 'CLOSE_CANCELLED':
      return { ...state, isConfirmingClose: false };
    case 'CLOSED_PANEL':
      return initialPanelState(state.baseline);
    case 'SAVE_STARTED':
      return { ...state, saveError: false, isSaving: true };
    case 'SAVE_FAILED':
      return { ...state, saveError: true };
    case 'SAVE_SETTLED':
      return { ...state, isSaving: false };
    default:
      return state;
  }
}
